import WidgetModel from './widget-model'
import _ from 'lodash'
import metadata from 'scoreboard/components/metadata'
import { showPropsByDomainType } from './shape/shape-model'
import { DATA_SOURCE_PROPERTY } from 'scoreboard/dashboards/widgets/shape/shape-widget-properties'
import { defaultDatasetSourceLabel } from './default-data-source-label'
import showPropertyToTextProperty from 'scoreboard/dashboards/widgets/shape/show-property-to-text-property'
import { bubblePropertyNames, sortShowProps } from 'scoreboard/dashboards/widgets/shape/sort-show-properties'
import calendars from 'scoreboard/components/calendars'
import momentHelper from 'scoreboard/components/moment-helper'
import initiativeCalculationTypes
	from 'scoreboard/initiatives/initiative-calculation-types'
import enums from 'scoreboard/components/enums'
import { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import formatInitCalcs
	from 'scoreboard/initiatives/format-initiative-calculations'
import DataType from 'scoreboard/datasets/model/data-type'
import config from '../../components/config'
import i18n from '../../components/i18n'
import resolveWidgetPeriod from './resolve-widget-period'
import Initiative from '../../initiatives/initiative'
import moment from 'moment'
import ScorecardNode from '../../scorecards/scorecard-node'
import { BEHIND_RED } from 'scoreboard/initiatives/schedule-status'
const { CURRENCY } = DataType

const {
	LABEL,
	DESCRIPTION,
	PERIOD,
	COLOR,
	ACTUAL,
	SCORE,
	TREND,
	WEIGHT,
	GOAL,
	START_DATE,
	END_DATE,
	PROJECTED_END_DATE,
	SCHEDULE,
	BUDGET
} = bubblePropertyNames

/**
 * Common functionality for widgets that have a data source (#52717)
 */
const DataSourceWidgetModel = WidgetModel.extend({

	/**
	 * Updates the properties of the widget to match the results of using
	 * DataSourceSelector
	 */
	setDataSource ({ domainType, dataSource, period }) {
		let update = {}

		if (_.isNil(domainType)) {
			// The "None" option. Need to set these props to null so that
			// Backbone clears the properties
			_.extend(update,{
				section: null,
				domainObjectId: null
			})
		} else if (domainType === 'dataset') {
			this.setDatasetDataSource(dataSource)
		} else {
			// For scorecards and initiatives, just set the section and
			// domainObjectId
			_.extend(update,{
				section: metadata[domainType].section,
				domainObjectId: dataSource.domainObjectId,
				[DATA_SOURCE_PROPERTY]: dataSource.dataProperty
			})
		}

		_.extend(update, period)

		this.properties.set(update)

		// Set any showX properties that aren't valid for the new data source
		// to false. Example: { showScore: false, showTrend: false } for
		// an initiative data source
		//
		// #58593 this has to be done after changing the data source itself,
		// because the valid properties can vary even within the same domainType
		// (eg showActual is valid for metrics but not other scorecard items)
		let validShowProps = this.validShowPropertyKeys()
		this.properties.set(_(this.properties.attributes)
			.keys()
			.filter(p => {
				return p.startsWith('show') && !validShowProps.includes(p)
			})
			.map(p => [p, false])
			.fromPairs()
			.value())
	},

	/**
	 * Returns the valid showX properties for the widget's current domainType.
	 * The default is to use the showPropsByDomainType from shape-model.js
	 * but this can be overridden by the widget model
	 *
	 * @return {String[]}
	 **/
	validShowPropertyKeys () {
		return showPropsByDomainType[this.domainType() ?? 'none']
	},

	setDatasetDataSource (dataSource) {
		// filters and aggregations have to be set separately so
		// that they become a JSON string as other parts of the code
		// expect
		this.filters(dataSource.filters)
		this.aggregations(dataSource.aggregations)

		let propsToSet = {
			section: 'datasets',
			// Start out explicitly setting these properties to null, so they are
			// cleared out if the newProps don't have them
			startingDatasetId: null,
			calendarFieldId: null,
			fieldId: null,
			visibleLinkedFieldId: null,
			equation: null
		}

		// Now add in new props, leaving out filters and aggregations (already
		// handled above)
		_.extend(propsToSet, _.omit(dataSource, 'filters', 'aggregations'))

		if (dataSource.startingDatasetId) {
			// #32053 the widget needs a 'domainObjectId' property; if the datasource
			// has a 'startingDatasetId', use that
			propsToSet.domainObjectId = dataSource.startingDatasetId
		} else if (!_.isEmpty(dataSource.aggregations)) {
			// #34283 if the data source is calculated and doesn't have a
			// startingDatasetId, just use one of the dataset IDs from
			// one of the aggregations (doesn't matter which one)
			propsToSet.domainObjectId = dataSource.aggregations[0].datasetId
		}

		let label = this.properties.get('label')
		if (label) {
			// #21215 if the widget has a label, see if it matches the default label
			// based on the old aggregation type. If so, then update the label using
			// the new aggregation type.
			let previousDefaultLabel = defaultDatasetSourceLabel(
				this.dataset(), this.properties)
			if (label === previousDefaultLabel) {
				// #34196 if we're changing the startingDatasetId, we need to
				// use that dataset to figure out the new default label
				let newDataset = propsToSet.startingDatasetId
					? this.data.datasetModel(propsToSet.startingDatasetId)
					: this.dataset()

				propsToSet.label = defaultDatasetSourceLabel(
					newDataset, propsToSet)
			} else if (propsToSet.dataSourceType === 'basic') {
				// #38205 If the old label had been the default label, we wouldn't be in
				// this else block, so if we're here, and current label is not
				// undefined, it must be because it was a custom label
				propsToSet.label = label || previousDefaultLabel
			}
		}

		this.properties.set(propsToSet)
	},


	/**
	 * For shapes with a dataset datasource, gets or sets dataset filters
	 * property. Handles converting to/from JSON string (which is how they are
	 * stored in the database)
	 */
	filters: function (newFilters) {
		return this._jsonProperty('filters', newFilters, [])
	},

	/**
	 * For shapes with a dataset datasource, gets or sets the aggregations
	 * property. Handles converting to/from JSON string (which is how they are
	 * stored in the database)
	 */
	aggregations: function(newAggregations) {
		return this._jsonProperty('aggregations', newAggregations, [])
	},

	/**
	 * Gets or sets the value for a property. Handles converting to/from JSON
	 * string (which is how they are stored in the database)
	 * @param {String} propertyName the name of the property
	 * @param {Object} newValue the new value for the property, if applicable
	 */
	_jsonProperty: function(propertyName, newValue, defaultValue) {
		if (_.isUndefined(newValue)) {
			let propertyValue = this.properties.get(propertyName)
			propertyValue = propertyValue ?? defaultValue
			if (_.isString(propertyValue)) {
				propertyValue = JSON.parse(propertyValue)
			}
			return propertyValue
		}

		this.properties.set(propertyName, JSON.stringify(newValue))
	},

	domainType () {
		if (this.isNoneSource()) {
			return null
		}

		return metadata[this.section()]?.domainType
	},

	/**
	 * Returns whether this shape is set to "None" for the datasource
	 *
	 * @return {boolean}
	 */
	isNoneSource () {
		return _.isNil(this.section())
	},

	section: function () {
		return this.properties.get('section')
	},

	/**
	 * For shapes with a dataset datasource, returns the dataset id. Note that
	 * for calculated sources, there could be many associated datasets; which one
	 * this returns is arbitrary. To get all associated dataset ids, use
	 * the datasetIds() function.
	 *
	 * @return {Int}
	 */
	datasetId: function () {
		return this.isDatasetSource()
			? parseInt(this.properties.get('domainObjectId'))
			: null
	},

	/**
	 * For shapes with a dataset datasource, returns all associated dataset ids.
	 * Unlike datasetId(), this accounts for calculated sources, which could
	 * have many associated datasets.
	 *
	 * @return {Int[]}
	 */
	datasetIds () {
		let ids = new Set()
		ids.add(this.datasetId())

		this.aggregations()?.forEach(agg => {
			ids.add(agg.datasetId)
		})

		return Array.from(ids).filter(id => !_.isNil(id))
	},

	/**
	 * For widgets with a dataset datasource, returns the dataset model. See
	 * datasetId() for details on calculated sources
	 *
	 * @returns {Dataset}
	 */
	dataset: function () {
		return this.data.datasetModel(this.datasetId())
	},

	/**
	 * For widgets with a scorecard item datasource, returns the scorecard item
	 * as a plain object.
	 *
	 * @returns {Object}
	 */
	scorecardItem() {
		return this.data.scorecards(this.properties.get('domainObjectId'))
	},

	/**
	 * Returns true if the widget's scorecard item functions like a metric
	 * (is a metric or is linked to a metric)
	 */
	metricish() {
		return this.section() === 'scorecards' &&
			this.scorecardItem() /* #58970 */ &&
			ScorecardNode.metricish(this.scorecardItem())
	},

	/**
	 * Returns whether this shape has a dataset datasource
	 *
	 * @return {boolean}
	 */
	isDatasetSource () {
		return this.section() === 'datasets'
	},

	domainObject: function () {
		if (this.isNoneSource()) {
			return null
		}

		return this.data[this.section()](this.properties.get('domainObjectId'))
	},

	/**
	 * Returns all valid options for `shapeTextProperty` (the dropdown in the
	 * Set Text menu) that are appropriate for the current data source.
	 *
	 * @return {{label: String, value: String}[]} options in the format
	 * expected by the selects() function
	 */
	validShapeTextPropertyOptions () {
		return this.flatShowProperties()
			.filter(showProp => {
				return showProp.property != 'showTrend'
			})
			.map(showProp => {
				return {
					value: showPropertyToTextProperty(showProp.property),
					label: i18n.get(showProp.label)
				}
			})
	},

	/**
	 * Returns array of all of the properties that can be set in the "Showing" menu. Format is:
	 * {
	 * 	property: 'showLabel', // the true/false property in widgetModel.properties
	 * 	label: 'common.name', // the i18n code describing the value
	 * 	legend: true, // true if the value shows up in the bubble's legend (could also float up to the bubble text)
	 * 	value: domainObject.label // the current period's value for the property (displayed in the legend/bubble)
	 * }
	 *
	 * @param {Number} periodId - if provided, returned object will contain a 'value'
	 * for that period; if omitted, 'value' will be null
	 */
	showProperties: function(periodId) {
		if (this.isDatasetSource() || this.isNoneSource()) {
			return []
		}

		var domainObject = this.domainObject() || {}
		const topLevelMenuProps = this.getTopLevelMenuProps(domainObject,
			periodId)

		const scheduleSubmenu = {
			property: SCHEDULE,
			type: 'submenu',
			label: 'common.schedule',
			target: this.getScheduleSubmenuProps(domainObject, periodId)
		}
		if (scheduleSubmenu.target) {
			topLevelMenuProps.push(scheduleSubmenu)
		}

		const budgetSubmenu = {
			property: BUDGET,
			type: 'submenu',
			label: 'common.budget',
			target: this.getBudgetSubmenuProps(domainObject, periodId)
		}
		if (budgetSubmenu.target) {
			topLevelMenuProps.push(budgetSubmenu)
		}

		return sortShowProps(topLevelMenuProps)
	},

	/**
	 * Wrapper around {@link showProperties} but flattens the submenu props'
	 * target array into one list
	 * @param periodId the period id used for returning value keys with the props
	 */
	flatShowProperties: function(periodId) {
		return _.flatMap(this.showProperties(periodId), prop => {
			if (prop.type === 'submenu') {
				return prop.target
			} else {
				return [prop]
			}
		})
	},

	getTopLevelMenuProps: function(domainObject, periodId) {
		const section = this.section()
		const topLevelMenuProps = []

		topLevelMenuProps.push({
			property: LABEL,
			label: 'common.name',
			legend: true,
			value: domainObject.label
		})

		if (section === 'scorecards' || section === 'initiatives') {
			topLevelMenuProps.push({
				property: DESCRIPTION,
				label: 'common.description',
				legend: true,
				value: domainObject.description
			})

			topLevelMenuProps.push({
				property: PERIOD,
				label: 'common.calendar.period',
				legend: true,
				value: periodId ? calendars.periodsById[periodId].label : null
			})
		}

		if (section === 'scorecards') {
			var scoreInfo = periodId ? this.formattedScoreInfo(domainObject, periodId) : {}
			topLevelMenuProps.push({
				property: COLOR,
				label: 'charts.chart.color',
				legend: true,
				value: this.getColorLabel(scoreInfo.performanceColor)
			})
			if (domainObject && (domainObject.type === 'metric' || domainObject.sourceType === 'metric')) {
				this.getMetricTopLevelProps(domainObject, scoreInfo)
					.forEach(p => topLevelMenuProps.push(p))
			}
			if (config.scoresVisible) {
				topLevelMenuProps.push({
					property: SCORE,
					label: 'scorecards.node.score',
					legend: true,
					value: scoreInfo.score,
					valueUnshortened: scoreInfo.scoreUnshortened
				})
			}
			topLevelMenuProps.push({
				property: TREND,
				label: 'scorecards.executiveView.trend',
				legend: false,
				value: momentHelper.formatNumericDate(domainObject.aggregatedEndDate)
			})
			topLevelMenuProps.push({
				property: WEIGHT,
				label: 'scorecards.node.weight',
				legend: true,
				value: scoreInfo.percentWeighting,
				valueUnshortened: scoreInfo.percentWeightingUnshortened
			})
		} else if (section === 'initiatives') {
			topLevelMenuProps.push({
				property: COLOR,
				label: 'charts.chart.color',
				legend: true,
				value: this.getColorLabel(this.getInitiativeColor())
			})

			const percentComplete = initiativeCalculationTypes.nonColorTypes()
				.find(t => t.type === 'percent-complete')
			if (percentComplete) {
				topLevelMenuProps.push(_.extend(this.getMenuItemProps(percentComplete), {
					value: this.getValuesForPropType(percentComplete,
						domainObject, periodId).unshortened
					}))
			}
		}

		return topLevelMenuProps
	},

	getMetricTopLevelProps: function (metric, scoreInfo) {
		let props = []
		props.push({
			property: ACTUAL,
			label: 'scorecards.node.metric.actualValue',
			legend: true,
			value: scoreInfo.value,
			valueUnshortened: scoreInfo.valueUnshortened
		})

		let scoringType = config.scoringType(metric.scoringTypeId)

		// If the metric has a goal threshold, add the showGoal option
		if (_.isNumber(scoringType.goalIndex)) {
			props.push({
				property: GOAL,
				// #33881 always use "Goal" for the label, even if the goal
				// threshold is usually called something else (like "Best")
				label: 'scorecards.node.metric.threshold.goal',
				legend: true,
				value: scoreInfo.goal,
				valueUnshortened: scoreInfo.goalUnshortened
			})
		}

		return props
	},

	getScheduleSubmenuProps: function(domainObject, periodId) {
		if (this.section() !== 'initiatives') {
			return
		}
		const scheduleSubmenuProps = []
		const calcs = this.getCalculations(domainObject, periodId)

		scheduleSubmenuProps.push({
			property: START_DATE,
			label: 'initiatives.startDate',
			legend: true,
			value: momentHelper.formatNumericDate(
				domainObject.aggregatedStartDate)
		})
		scheduleSubmenuProps.push({
			property: END_DATE,
			label: 'initiatives.endDate',
			legend: true,
			value: momentHelper.formatNumericDate(
				domainObject.aggregatedEndDate)
		})
		scheduleSubmenuProps.push({
			property: PROJECTED_END_DATE,
			label: 'initiatives.projectedEndDate',
			legend: true,
			value: momentHelper.formatNumericDate(calcs.projectedEndDate)
		})
		initiativeCalculationTypes.nonColorTypes().filter(t =>
				t.type == 'schedule-variance' ||
				t.type == 'percent-time-elapsed' ||
				t.type == 'days-elapsed')
			.forEach(type => {
				let values = this.getValuesForPropType(
					type, domainObject, periodId)

				scheduleSubmenuProps.push(
					_.extend(this.getMenuItemProps(type), {
						value: values.formatted,
						valueShortened: values.shortened,
						valueUnshortened: values.unshortened
					}))
			})

		return sortShowProps(scheduleSubmenuProps, SCHEDULE)
	},

	getBudgetSubmenuProps: function(domainObject, periodId) {
		if (this.section() !== 'initiatives') {
			return
		}

		const budgetSubmenuProps = []
		const shorten = !!this.properties.get('showAbbreviatedNumbers')

		let budgetShortenedAndUnshortened = shortenedAndUnshortened({
			value: domainObject.aggregatedBudget,
			dataType: CURRENCY,
			currencyCode: domainObject.currencyCode
		})
		budgetSubmenuProps.push({
			property: initiativeCalculationTypes.showPropertyName('budget'),
			label: 'initiatives.budget',
			legend: true,
			value: formatInitCalcs('budget')(domainObject.aggregatedBudget,
				{
					currencyCode: domainObject.currencyCode,
					shorten
				}),
			valueShortened: budgetShortenedAndUnshortened.shortened,
			valueUnshortened: budgetShortenedAndUnshortened.unshortened
		})

		let ignore = ['budget', 'schedule-variance', 'percent-time-elapsed',
			'days-elapsed', 'percent-complete', 'projected-end-date']
		initiativeCalculationTypes.nonColorTypes()
			.filter(t => !_.includes(ignore, t.type))
			.forEach(type => {
				const {shortened, unshortened, formatted } = this.getValuesForPropType(
					type, domainObject, periodId)
				budgetSubmenuProps.push(_.extend(this.getMenuItemProps(type), {
					value: formatted,
					valueShortened: shortened,
					valueUnshortened: unshortened
				}))
		})

		return sortShowProps(budgetSubmenuProps, BUDGET)
	},

	getColorLabel: function(color) {
		if (_.isNil(color)) {
			return ''
		} else if (color === 'gray') {
			// #38210 If it's gray, we want to display its text color.
			return i18n.get('scorecards.color.gray')
		} else {
			return i18n.get(enums.scoreColor[color])
		}
	},

	getInitiativeColor: function () {
		let periodId = resolveWidgetPeriod(this)

		let domainObject = this.domainObject()
		if (!periodId || !domainObject) {
			return NO_DATA_COLOR
		}

		let calcs = domainObject.calculations && domainObject.calculations[periodId]

		let initModel = new Initiative(domainObject)

		let today = moment()
		let periodEnd = calendars.periodsById[periodId].end

		// If today is in the current period, or we're in a future period,
		// we want to use today as a reference date (so it will turn red
		// automatically if the due date has passed.
		//
		// However, if we're looking at a period in the past, we want it
		// to stay whatever color it was then, so use that period's end
		// date for a reference date.
		let referenceDate = periodEnd.isAfter(today) ? today : periodEnd

		let color
		let pastDueDateStatus = initModel.getPastDueDateStatus({ referenceDate })
		if (pastDueDateStatus) {
			color = pastDueDateStatus === BEHIND_RED
				? 'red-medium' : 'yellow-medium'
		} else {
			color = calcs && calcs.color
		}

		// Not sure if this will ever occur, but 'gray' is our 'not enough data'
		// color, so default to that
		return color ? color : NO_DATA_COLOR
	},

	getMenuItemProps: function(type) {
		return {
			property: initiativeCalculationTypes.showPropertyName(type.type),
			label: type.messageCode,
			legend: true
		}
	},

	getValuesForPropType: function(type, domainObject, periodId) {
		let calcs = this.getCalculations(domainObject, periodId)
		let raw = calcs.calculations && calcs.calculations[type.type]
			,	shortendUnshortendRaw = raw
		// budget and schedule variance values should be presented as their absolute value
		if (type.type == 'budget-variance-percent' ||
			type.type == 'budget-variance' ||
			type.type == 'schedule-variance') {
			shortendUnshortendRaw = Math.abs(raw)
		}

		let formatter = formatInitCalcs(type.type)
		let formatted = formatter(raw, {
			shorten: !!this.properties.get('showAbbreviatedNumbers'),
			currencyCode: domainObject.currencyCode
		})

		let shortAndUnshort = shortenedAndUnshortened({
			value: shortendUnshortendRaw,
			dataType: type.dataType,
			currencyCode: domainObject.currencyCode
		})

		return _.extend(shortAndUnshort, { formatted })
	},

	getCalculations: function(domainObject, periodId) {
		let calcs = domainObject.calculations && domainObject.calculations[periodId]
		calcs = calcs || {}
		return calcs
	},

	/**
	 * For widgets with a dataset datasource, returns the dataset request settings
	 * to be used in the API call
	 */
	datasetRequest: function () {
		return {
			id: this.datasetId(),
			selectedCalendarFieldId: this.properties.get('calendarFieldId'),
			aggregationType: this.properties.get('aggregationType'),
			aggregationFieldId: this.properties.get('fieldId'),
			aggregationVisibleLinkedFieldId: this.properties.get('visibleLinkedFieldId'),
			filters: this.filters(),
			equation: this.properties.get('equation'),
			aggregations: this.aggregations()
		}
	},

})

const NO_DATA_COLOR = 'gray'

export default DataSourceWidgetModel
export { NO_DATA_COLOR }

DataSourceWidgetModel.__filename__ = 'dashboards/widgets/data-source-widget-model.js'
